import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import CustomSelect from './CustomSelect';

interface StaffMember {
  id: number;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface AssignedResident {
  id: number;
  name: string;
  age: number;
  roomNumber: string;
  photo?: string;
}

interface RecentActivity {
  id: number;
  residentName?: string;
  residentId?: number;
  createdAt?: string;
  completedAt?: string;
  [key: string]: any;
}

interface StaffProfileData {
  staff: StaffMember;
  assignedResidents: AssignedResident[];
  recentActivities: {
    notes: RecentActivity[];
    incidents: RecentActivity[];
    tasks: RecentActivity[];
    careCompletions: RecentActivity[];
    appointments: RecentActivity[];
  };
  stats: {
    totalAssignedResidents: number;
    totalNotes: number;
    totalIncidents: number;
    totalTasks: number;
    totalCareCompletions: number;
    totalAppointments: number;
  };
}

interface StaffDetailsModalProps {
  staffId: number;
  isOpen: boolean;
  onClose: () => void;
}

const StaffDetailsModal: React.FC<StaffDetailsModalProps> = ({ staffId, isOpen, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [profileData, setProfileData] = useState<StaffProfileData | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [allResidents, setAllResidents] = useState<{id: number; name: string; age: number}[]>([]);
  const [selectedResidentId, setSelectedResidentId] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);
  const [assignMessage, setAssignMessage] = useState('');

  useEffect(() => {
    if (isOpen && staffId) {
      fetchStaffProfile();
    }
  }, [isOpen, staffId]);

  const fetchStaffProfile = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get(`/users/staff/${staffId}/profile`);
      
      if (response.data.success) {
        setProfileData(response.data.data);
      }
    } catch (err: any) {
      console.error('Error fetching staff profile:', err);
      setError(err.response?.data?.message || 'Failed to load staff profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllResidents = async () => {
    try {
      const response = await api.get('/residents?limit=100');
      if (response.data.success) {
        setAllResidents(response.data.data.residents || []);
      }
    } catch (err) {
      console.error('Error fetching residents:', err);
    }
  };

  const handleAssignResident = async () => {
    if (!selectedResidentId) return;
    try {
      setAssignLoading(true);
      setAssignMessage('');
      await api.post('/users/staff/assign-resident', {
        staffId: staffId,
        residentId: parseInt(selectedResidentId)
      });
      setAssignMessage('Resident assigned successfully!');
      setSelectedResidentId('');
      setShowAssignModal(false);
      fetchStaffProfile();
    } catch (err: any) {
      setAssignMessage(err.response?.data?.message || 'Failed to assign resident');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveAssignment = async (residentId: number) => {
    if (!window.confirm('Remove this resident assignment?')) return;
    try {
      await api.delete('/users/staff/remove-assignment', {
        data: { staffId: staffId, residentId: residentId }
      });
      fetchStaffProfile();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to remove assignment');
    }
  };

  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const formatDateTime = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-primary-100 text-primary-800';
      case 'staff':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'normal':
        return 'bg-blue-100 text-blue-800';
      case 'low':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'residents', label: 'Assigned Residents' },
    { id: 'activities', label: 'Recent Activities' },
    { id: 'stats', label: 'Statistics' }
  ];

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white card-modal shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-4">
            <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-2xl font-bold text-blue-600">
                {profileData?.staff?.name?.charAt(0).toUpperCase() || '?'}
              </span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {profileData?.staff?.name || 'Loading...'}
              </h2>
              <p className="text-sm text-gray-500">{profileData?.staff?.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-6">
          <nav className="-mb-px flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm transition-colors`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading staff details...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
                <p className="mt-1 text-sm text-gray-500">{error}</p>
                <button
                  onClick={fetchStaffProfile}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : !profileData ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No data available</h3>
                <p className="mt-1 text-sm text-gray-500">Unable to load staff profile information</p>
                <button
                  onClick={fetchStaffProfile}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Role</h5>
                      <span className={`inline-flex px-3 py-1 text-sm leading-5 font-semibold rounded-full ${getRoleBadgeColor(profileData.staff.role)}`}>
                        {profileData.staff.role}
                      </span>
                    </div>

                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Status</h5>
                      <span className={`inline-flex px-3 py-1 text-sm leading-5 font-semibold rounded-full ${
                        profileData.staff.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {profileData.staff.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Joined</h5>
                      <p className="text-gray-700">{formatDate(profileData.staff.createdAt)}</p>
                    </div>

                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Last Updated</h5>
                      <p className="text-gray-700">{formatDate(profileData.staff.updatedAt)}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Email</h5>
                      <p className="text-gray-700 bg-gray-50 p-3 rounded-lg break-all">
                        {profileData.staff.email}
                      </p>
                    </div>

                    <div>
                      <h5 className="text-sm font-semibold text-gray-900 mb-2">Quick Stats</h5>
                      <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg space-y-2">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">Assigned Residents:</span>
                          <span className="text-sm font-semibold text-gray-900">{profileData.stats.totalAssignedResidents}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">Recent Notes:</span>
                          <span className="text-sm font-semibold text-gray-900">{profileData.stats.totalNotes}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-700">Care Completions:</span>
                          <span className="text-sm font-semibold text-gray-900">{profileData.stats.totalCareCompletions}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Assigned Residents Tab */}
              {activeTab === 'residents' && (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-gray-900">
                      Assigned Residents ({profileData.assignedResidents.length})
                    </h4>
                    <button
                      onClick={() => { setShowAssignModal(true); fetchAllResidents(); setAssignMessage(''); }}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                    >
                      + Assign Resident
                    </button>
                  </div>

                  {assignMessage && (
                    <div className={`mb-4 p-3 rounded-lg text-sm ${assignMessage.includes('success') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      {assignMessage}
                    </div>
                  )}

                  {showAssignModal && (
                    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h5 className="text-sm font-semibold text-gray-900 mb-3">Select a Resident to Assign</h5>
                      <div className="flex items-center gap-3 flex-wrap sm:flex-nowrap">
                        <div className="flex-1 min-w-[200px] w-full">
                          <CustomSelect
                            value={selectedResidentId}
                            onChange={setSelectedResidentId}
                            placeholder="-- Select Resident --"
                            options={[
                              { value: '', label: '-- Select Resident --' },
                              ...allResidents
                                .filter(r => !profileData.assignedResidents.some(ar => ar.id === r.id))
                                .map(r => ({ value: String(r.id), label: `${r.name} (Age ${r.age})` })),
                            ]}
                          />
                        </div>
                        <button
                          onClick={handleAssignResident}
                          disabled={!selectedResidentId || assignLoading}
                          className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50"
                        >
                          {assignLoading ? 'Assigning...' : 'Assign'}
                        </button>
                        <button
                          onClick={() => setShowAssignModal(false)}
                          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {profileData.assignedResidents.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {profileData.assignedResidents.map((resident) => (
                        <div key={resident.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                                <span className="text-lg font-bold text-green-600">{resident.name?.charAt(0)}</span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-gray-900">{resident.name}</p>
                                <p className="text-xs text-gray-500">Age {resident.age}</p>
                              </div>
                            </div>
                            <button
                              onClick={() => handleRemoveAssignment(resident.id)}
                              className="text-red-400 hover:text-red-600 transition-colors"
                              title="Remove assignment"
                            >
                              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-8">No residents assigned. Click "Assign Resident" to add one.</p>
                  )}
                </div>
              )}

              {/* Recent Activities Tab */}
              {activeTab === 'activities' && (
                <div className="space-y-4">
                  {/* Recent Notes */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Recent Notes</h4>
                    {profileData.recentActivities.notes.length > 0 ? (
                      <div className="space-y-3">
                        {profileData.recentActivities.notes.map((note) => (
                          <div key={note.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">{note.residentName}</span>
                                <span className={`px-2 py-0.5 text-xs rounded-full ${getPriorityBadgeColor(note.priority)}`}>
                                  {note.priority}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">{formatDateTime(note.createdAt)}</span>
                            </div>
                            <p className="text-sm text-gray-700 line-clamp-2">{note.content}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No recent notes</p>
                    )}
                  </div>

                  {/* Recent Incidents */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Recent Incidents Reported</h4>
                    {profileData.recentActivities.incidents.length > 0 ? (
                      <div className="space-y-3">
                        {profileData.recentActivities.incidents.map((incident) => (
                          <div key={incident.id} className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex items-center space-x-2">
                                <span className="text-sm font-medium text-gray-900">{incident.residentName}</span>
                                <span className="px-2 py-0.5 text-xs rounded-full bg-red-100 text-red-800">
                                  {incident.severity}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">{formatDate(incident.incidentDate)}</span>
                            </div>
                            <p className="text-sm text-gray-700">{incident.incidentType}</p>
                            <p className="text-sm text-gray-600 line-clamp-1 mt-1">{incident.description}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No recent incidents</p>
                    )}
                  </div>

                  {/* Recent Tasks */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Assigned Tasks</h4>
                    {profileData.recentActivities.tasks.length > 0 ? (
                      <div className="space-y-3">
                        {profileData.recentActivities.tasks.map((task) => (
                          <div key={task.id} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex justify-between items-start mb-2">
                              <span className="text-sm font-medium text-gray-900">{task.title}</span>
                              <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadgeColor(task.status)}`}>
                                {task.status}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs text-gray-600">
                              <span>{task.category}</span>
                              <span>Due: {formatDate(task.dueDate)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No assigned tasks</p>
                    )}
                  </div>

                  {/* Recent Care Completions */}
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Recent Care Activities</h4>
                    {profileData.recentActivities.careCompletions.length > 0 ? (
                      <div className="space-y-3">
                        {profileData.recentActivities.careCompletions.map((care) => (
                          <div key={care.id} className="bg-green-50 border border-green-200 rounded-lg p-4">
                            <div className="flex justify-between items-start">
                              <div>
                                <span className="text-sm font-medium text-gray-900">{care.activityName}</span>
                                <p className="text-xs text-gray-600 mt-1">{care.residentName}</p>
                              </div>
                              <span className="text-xs text-gray-500">{formatDateTime(care.completedAt)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 text-center py-4">No recent care activities</p>
                    )}
                  </div>
                </div>
              )}

              {/* Statistics Tab */}
              {activeTab === 'stats' && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-4">Performance Statistics</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3.5">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
                      <div className="text-3xl font-bold text-blue-600">{profileData.stats.totalAssignedResidents}</div>
                      <div className="text-sm text-gray-600 mt-2">Assigned Residents</div>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                      <div className="text-3xl font-bold text-green-600">{profileData.stats.totalNotes}</div>
                      <div className="text-sm text-gray-600 mt-2">Notes Created</div>
                    </div>
                    <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 text-center">
                      <div className="text-3xl font-bold text-primary-600">{profileData.stats.totalCareCompletions}</div>
                      <div className="text-sm text-gray-600 mt-2">Care Activities Completed</div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                      <div className="text-3xl font-bold text-yellow-600">{profileData.stats.totalTasks}</div>
                      <div className="text-sm text-gray-600 mt-2">Tasks Assigned</div>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                      <div className="text-3xl font-bold text-red-600">{profileData.stats.totalIncidents}</div>
                      <div className="text-sm text-gray-600 mt-2">Incidents Reported</div>
                    </div>
                    <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 text-center">
                      <div className="text-3xl font-bold text-indigo-600">{profileData.stats.totalAppointments}</div>
                      <div className="text-sm text-gray-600 mt-2">Appointments Scheduled</div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default StaffDetailsModal;
